import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { baseStyles, badgeStyles } from '../styles/shared.js';
import type {
  IngestionJournal,
  JournalStep,
  JournalStepDocumentUploaded,
  JournalStepTextExtracted,
  JournalStepRequirementsExtracted,
  JournalStepUserReview,
  JournalStepCodeGenerated,
  JournalStepUserCommitted,
  JournalStepError,
  ClaudeApiUsage,
  ExtractedRequirement,
  EditDelta,
} from '../api/ingestion-types.js';

// ---------------------------------------------------------------------------
// <sysml-audit-trail> — Full-page ingestion audit trail overlay
//
// Renders each ingestion step as a rich card with collapsible content
// sections, asset previews, code blocks, requirement tables, and
// WHAT/WHY/HOW labels. Follows sysml-command-palette.ts overlay pattern.
// ---------------------------------------------------------------------------

@customElement('sysml-audit-trail')
export class SysmlAuditTrail extends LitElement {
  static override styles = [
    baseStyles,
    badgeStyles,
    css`
      :host {
        position: fixed;
        inset: 0;
        z-index: 9999;
        display: none;
        justify-content: center;
        align-items: flex-start;
        padding: 32px;
        pointer-events: none;
      }
      :host([open]) {
        display: flex;
        pointer-events: auto;
      }

      /* ── Backdrop ── */
      .backdrop {
        position: absolute;
        inset: 0;
        background: color-mix(in srgb, var(--r5-surface) 70%, transparent);
        backdrop-filter: blur(4px);
      }

      /* ── Main container ── */
      .trail-container {
        position: relative;
        width: 100%;
        max-width: 860px;
        max-height: calc(100vh - 64px);
        display: flex;
        flex-direction: column;
        background: var(--r5-surface);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-lg);
        box-shadow: 0 16px 64px color-mix(in srgb, var(--r5-surface) 30%, black);
        overflow: hidden;
      }

      /* ── Header ── */
      .trail-header {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-3) var(--r5-space-4);
        border-bottom: 1px solid var(--r5-border);
        flex-shrink: 0;
      }
      .trail-header-icon {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        color: var(--r5-primary);
      }
      .trail-title {
        font-size: var(--r5-font-sm);
        font-weight: 600;
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .trail-status-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 8px;
        border-radius: var(--r5-radius-sm);
        font-size: var(--r5-font-size-badge);
        font-weight: 500;
      }
      .status-live {
        background: color-mix(in srgb, var(--r5-primary) 12%, transparent);
        color: var(--r5-primary);
      }
      .status-committed {
        background: var(--r5-success-50);
        color: var(--r5-success-700);
      }
      .status-cancelled {
        background: var(--r5-warning-50);
        color: var(--r5-warning-700);
      }
      .status-error {
        background: var(--r5-destructive-50);
        color: var(--r5-destructive-700);
      }
      .trail-close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border: none;
        background: transparent;
        color: var(--r5-text-muted);
        cursor: pointer;
        border-radius: var(--r5-radius-sm);
        padding: 0;
        flex-shrink: 0;
      }
      .trail-close:hover {
        background: color-mix(in srgb, var(--r5-text) 10%, transparent);
        color: var(--r5-text);
      }
      .trail-subtitle {
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
        padding: var(--r5-space-1) var(--r5-space-4) var(--r5-space-2);
        border-bottom: 1px solid var(--r5-border);
        flex-shrink: 0;
      }

      /* ── Scrollable body ── */
      .trail-body {
        flex: 1;
        overflow-y: auto;
        padding: var(--r5-space-3) var(--r5-space-4);
      }

      /* ── Step card ── */
      .step-card {
        margin-bottom: var(--r5-space-3);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        overflow: hidden;
      }
      .step-card-header {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-2) var(--r5-space-3);
        cursor: pointer;
        user-select: none;
        transition: background var(--r5-duration-fast) var(--r5-ease-default);
      }
      .step-card-header:hover {
        background: color-mix(in srgb, var(--r5-text) 3%, transparent);
      }
      .step-number {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: var(--r5-primary);
        color: white;
        font-size: 11px;
        font-weight: 600;
        flex-shrink: 0;
      }
      .step-number.error {
        background: var(--r5-destructive-500);
      }
      .step-card-title {
        font-size: var(--r5-text-xs);
        font-weight: 600;
        flex: 1;
      }
      .step-card-duration {
        font-size: var(--r5-font-size-badge);
        font-family: var(--r5-font-mono);
        color: var(--r5-text-muted);
        flex-shrink: 0;
      }
      .step-chevron {
        width: 14px;
        height: 14px;
        color: var(--r5-text-muted);
        transition: transform var(--r5-duration-fast) var(--r5-ease-default);
        flex-shrink: 0;
      }
      .step-chevron.expanded {
        transform: rotate(90deg);
      }

      /* ── Step content (expanded) ── */
      .step-content {
        padding: 0 var(--r5-space-3) var(--r5-space-3);
      }

      /* ── WHAT/WHY/HOW grid ── */
      .wwh-grid {
        display: grid;
        grid-template-columns: 48px 1fr;
        gap: var(--r5-space-1) var(--r5-space-2);
        margin-bottom: var(--r5-space-2);
        font-size: var(--r5-text-xs);
      }
      .wwh-label {
        font-weight: 700;
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding-top: 1px;
      }
      .wwh-value {
        color: var(--r5-text);
        line-height: 1.5;
      }

      /* ── Content block ── */
      .content-block {
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        margin-bottom: var(--r5-space-2);
        overflow: hidden;
      }
      .content-block-header {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-1) var(--r5-space-2);
        background: color-mix(in srgb, var(--r5-text) 3%, transparent);
        font-size: var(--r5-font-size-badge);
        font-weight: 600;
        color: var(--r5-text-muted);
        border-bottom: 1px solid var(--r5-border);
      }
      .content-block-body {
        max-height: 400px;
        overflow-y: auto;
        padding: var(--r5-space-2);
        font-size: var(--r5-text-xs);
      }

      /* ── Code block ── */
      .code-block {
        font-family: var(--r5-font-mono);
        font-size: 12px;
        line-height: 1.5;
        white-space: pre-wrap;
        word-break: break-all;
        color: var(--r5-text);
        background: color-mix(in srgb, var(--r5-text) 3%, transparent);
        padding: var(--r5-space-2);
        border-radius: var(--r5-radius-sm);
        overflow-x: auto;
      }

      /* ── Image thumbnail ── */
      .thumbnail-row {
        display: flex;
        gap: var(--r5-space-3);
        align-items: flex-start;
        margin-bottom: var(--r5-space-2);
      }
      .thumbnail-img {
        max-width: 200px;
        border-radius: var(--r5-radius-md);
        border: 1px solid var(--r5-border);
      }
      .thumbnail-meta {
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-1);
        font-size: var(--r5-text-xs);
      }
      .meta-row {
        display: flex;
        gap: var(--r5-space-2);
      }
      .meta-label {
        color: var(--r5-text-muted);
        min-width: 70px;
        flex-shrink: 0;
      }
      .meta-value {
        color: var(--r5-text);
        font-family: var(--r5-font-mono);
      }

      /* ── API badges ── */
      .api-badges {
        display: flex;
        gap: var(--r5-space-1);
        flex-wrap: wrap;
        margin-top: var(--r5-space-2);
      }
      .api-badge {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        padding: 1px 6px;
        border-radius: var(--r5-radius-sm);
        font-family: var(--r5-font-mono);
        font-size: var(--r5-font-size-badge);
        background: color-mix(in srgb, var(--r5-primary) 8%, transparent);
        color: var(--r5-text-muted);
      }

      /* ── Requirement cards ── */
      .req-card {
        padding: var(--r5-space-2);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-sm);
        margin-bottom: var(--r5-space-1);
      }
      .req-card-name {
        font-weight: 600;
        font-size: var(--r5-text-xs);
      }
      .req-card-title {
        font-size: var(--r5-text-xs);
        color: var(--r5-text-muted);
        margin-top: 2px;
      }
      .req-card-text {
        font-size: var(--r5-font-size-badge);
        margin-top: var(--r5-space-1);
        line-height: 1.4;
      }
      .req-badges {
        display: flex;
        gap: var(--r5-space-1);
        flex-wrap: wrap;
        margin-top: var(--r5-space-1);
      }
      .req-badge {
        display: inline-flex;
        padding: 1px 5px;
        border-radius: var(--r5-radius-sm);
        font-size: var(--r5-font-size-badge);
        font-family: var(--r5-font-mono);
      }
      .req-badge-priority { background: color-mix(in srgb, var(--r5-warning-500) 12%, transparent); color: var(--r5-warning-700); }
      .req-badge-confidence { background: color-mix(in srgb, var(--r5-success-500) 12%, transparent); color: var(--r5-success-700); }
      .req-badge-method { background: color-mix(in srgb, var(--r5-primary) 12%, transparent); color: var(--r5-primary); }
      .req-badge-source { background: color-mix(in srgb, var(--r5-text) 8%, transparent); color: var(--r5-text-muted); }

      /* ── Decision list ── */
      .decision-list {
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-1);
      }
      .decision-item {
        display: flex;
        align-items: flex-start;
        gap: var(--r5-space-2);
        font-size: var(--r5-text-xs);
        padding: var(--r5-space-1) 0;
      }
      .decision-icon {
        width: 14px;
        height: 14px;
        flex-shrink: 0;
        margin-top: 1px;
      }
      .decision-accepted .decision-icon { color: var(--r5-success-700); }
      .decision-rejected .decision-icon { color: var(--r5-text-muted); }
      .decision-edited .decision-icon { color: var(--r5-primary); }
      .decision-removed .decision-icon { color: var(--r5-destructive-700); }
      .delta-before {
        text-decoration: line-through;
        color: var(--r5-destructive-700);
        font-family: var(--r5-font-mono);
        font-size: var(--r5-font-size-badge);
      }
      .delta-after {
        color: var(--r5-success-700);
        font-family: var(--r5-font-mono);
        font-size: var(--r5-font-size-badge);
      }
      .delta-row {
        display: flex;
        align-items: center;
        gap: var(--r5-space-1);
        margin-left: 22px;
        font-size: var(--r5-font-size-badge);
      }
      .delta-field {
        color: var(--r5-text-muted);
        min-width: 50px;
      }
      .delta-arrow {
        color: var(--r5-text-muted);
      }

      /* ── Diagnostics ── */
      .diag-row {
        display: flex;
        gap: var(--r5-space-2);
        font-size: var(--r5-font-size-badge);
        padding: 2px 0;
        font-family: var(--r5-font-mono);
      }
      .diag-error { color: var(--r5-destructive-700); }
      .diag-warning { color: var(--r5-warning-700); }
      .diag-info { color: var(--r5-text-muted); }

      /* ── Summary footer ── */
      .trail-footer {
        display: flex;
        align-items: center;
        gap: var(--r5-space-3);
        padding: var(--r5-space-2) var(--r5-space-4);
        border-top: 1px solid var(--r5-border);
        flex-shrink: 0;
        flex-wrap: wrap;
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
        background: color-mix(in srgb, var(--r5-text) 2%, transparent);
      }
      .footer-stat {
        display: flex;
        align-items: center;
        gap: var(--r5-space-1);
      }
      .footer-stat-value {
        font-weight: 600;
        color: var(--r5-text);
        font-family: var(--r5-font-mono);
      }

      /* ── Preview-only badge ── */
      .preview-only {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 6px;
        border-radius: var(--r5-radius-sm);
        font-size: var(--r5-font-size-badge);
        background: color-mix(in srgb, var(--r5-warning-500) 10%, transparent);
        color: var(--r5-warning-700);
        margin-bottom: var(--r5-space-2);
      }

      /* ── Gen options ── */
      .gen-options {
        display: flex;
        gap: var(--r5-space-2);
        flex-wrap: wrap;
        margin-bottom: var(--r5-space-2);
        font-size: var(--r5-font-size-badge);
      }
      .gen-opt {
        padding: 1px 6px;
        border-radius: var(--r5-radius-sm);
        background: color-mix(in srgb, var(--r5-text) 5%, transparent);
        color: var(--r5-text-muted);
      }
    `,
  ];

  /** The journal to display */
  @property({ attribute: false }) journal: IngestionJournal | null = null;

  /** Whether the overlay is open */
  @property({ type: Boolean, reflect: true }) open = false;

  /** Set of collapsed step IDs */
  @state() private _collapsedSteps = new Set<string>();

  override render() {
    if (!this.journal) return nothing;
    const j = this.journal;

    return html`
      <div class="backdrop" @click=${this._onClose}></div>
      <div class="trail-container">
        ${this._renderHeader(j)}
        ${this._renderSubtitle(j)}
        <div class="trail-body">
          ${j.steps.map((step, idx) => this._renderStepCard(step, idx))}
        </div>
        ${this._renderFooter(j)}
      </div>
    `;
  }

  // ── Header ──────────────────────────────────────────────────────────────

  private _renderHeader(j: IngestionJournal) {
    const statusClass = !j.completedAt ? 'status-live'
      : j.outcome === 'committed' ? 'status-committed'
      : j.outcome === 'error' ? 'status-error'
      : 'status-cancelled';
    const statusLabel = !j.completedAt ? 'Live'
      : j.outcome === 'committed' ? 'Committed'
      : j.outcome === 'error' ? 'Error'
      : 'Cancelled';

    return html`
      <div class="trail-header">
        <svg class="trail-header-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10 9 9 9 8 9"/>
        </svg>
        <span class="trail-title">Ingestion Audit Trail — ${j.filename}</span>
        <span class="trail-status-badge ${statusClass}">${statusLabel}</span>
        <button class="trail-close" @click=${this._onClose} title="Close (Esc)">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    `;
  }

  private _renderSubtitle(j: IngestionJournal) {
    const m = j.metrics;
    const startedTime = new Date(j.startedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    const parts: string[] = [`Started ${startedTime}`];
    if (m.apiCallCount > 0) parts.push(`${m.apiCallCount} API call${m.apiCallCount !== 1 ? 's' : ''}`);
    const totalTokens = m.totalInputTokens + m.totalOutputTokens;
    if (totalTokens > 0) parts.push(`${totalTokens.toLocaleString()} tokens`);

    return html`<div class="trail-subtitle">${parts.join(' · ')}</div>`;
  }

  // ── Step card ──────────────────────────────────────────────────────────

  private _renderStepCard(step: JournalStep, index: number) {
    const collapsed = this._collapsedSteps.has(step.id);
    const isError = step.kind === 'error';

    return html`
      <div class="step-card">
        <div class="step-card-header" @click=${() => this._toggleStep(step.id)}>
          <span class="step-number ${isError ? 'error' : ''}">${index + 1}</span>
          <span class="step-card-title">${this._stepTitle(step)}</span>
          <span class="step-card-duration">${this._formatDuration(step.durationMs)}</span>
          <svg class="step-chevron ${collapsed ? '' : 'expanded'}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </div>
        ${!collapsed ? html`<div class="step-content">${this._renderStepContent(step)}</div>` : nothing}
      </div>
    `;
  }

  private _renderStepContent(step: JournalStep) {
    switch (step.kind) {
      case 'document-uploaded': return this._renderDocumentUploaded(step);
      case 'text-extracted': return this._renderTextExtracted(step);
      case 'requirements-extracted': return this._renderRequirementsExtracted(step);
      case 'user-review': return this._renderUserReview(step);
      case 'code-generated': return this._renderCodeGenerated(step);
      case 'user-committed': return this._renderUserCommitted(step);
      case 'error': return this._renderError(step);
      default: return nothing;
    }
  }

  // ── Document Uploaded ──────────────────────────────────────────────────

  private _renderDocumentUploaded(step: JournalStepDocumentUploaded) {
    return html`
      <div class="wwh-grid">
        <span class="wwh-label">WHAT</span>
        <span class="wwh-value">Loaded "${step.filename}" (${this._formatBytes(step.sizeBytes)} ${step.sourceType})</span>
      </div>
      <div class="thumbnail-row">
        ${step.thumbnail
          ? html`<img class="thumbnail-img" src="data:image/jpeg;base64,${step.thumbnail}" alt="Uploaded document thumbnail" />`
          : nothing}
        <div class="thumbnail-meta">
          <div class="meta-row"><span class="meta-label">Type</span><span class="meta-value">${step.sourceType} (${step.mimeType})</span></div>
          <div class="meta-row"><span class="meta-label">Size</span><span class="meta-value">${this._formatBytes(step.sizeBytes)}</span></div>
          ${step.imageDimensions ? html`
            <div class="meta-row"><span class="meta-label">Dimensions</span><span class="meta-value">${step.imageDimensions.w} x ${step.imageDimensions.h} px</span></div>
          ` : nothing}
          ${step.dataHash ? html`
            <div class="meta-row"><span class="meta-label">SHA-256</span><span class="meta-value" title=${step.dataHash}>${step.dataHash.slice(0, 16)}...</span></div>
          ` : nothing}
        </div>
      </div>
    `;
  }

  // ── Text Extracted ─────────────────────────────────────────────────────

  private _renderTextExtracted(step: JournalStepTextExtracted) {
    const textSource = step.fullText ?? step.textPreview;
    const isPreviewOnly = !step.fullText;

    return html`
      <div class="wwh-grid">
        <span class="wwh-label">WHAT</span>
        <span class="wwh-value">Extracted ${step.textLength.toLocaleString()} characters of text from the uploaded document</span>
        <span class="wwh-label">WHY</span>
        <span class="wwh-value">Raw text serves as input for AI requirement extraction</span>
        <span class="wwh-label">HOW</span>
        <span class="wwh-value">${step.method === 'vision' ? 'Claude Vision API analyzed the image and produced structured text' : 'Text passed through directly (no AI processing needed)'}</span>
      </div>
      ${isPreviewOnly ? html`<span class="preview-only">Preview only — full data not retained for past sessions</span>` : nothing}
      ${step.promptSent ? html`
        <div class="content-block">
          <div class="content-block-header">Prompt Sent</div>
          <div class="content-block-body"><div class="code-block">${step.promptSent}</div></div>
        </div>
      ` : nothing}
      <div class="content-block">
        <div class="content-block-header">Extracted Text (${step.textLength.toLocaleString()} chars)</div>
        <div class="content-block-body"><div class="code-block">${textSource}</div></div>
      </div>
      ${step.apiUsage ? this._renderApiBadges(step.apiUsage) : nothing}
    `;
  }

  // ── Requirements Extracted ─────────────────────────────────────────────

  private _renderRequirementsExtracted(step: JournalStepRequirementsExtracted) {
    const hasFullData = !!step.fullRequirements;

    return html`
      <div class="wwh-grid">
        <span class="wwh-label">WHAT</span>
        <span class="wwh-value">Identified ${step.count} requirements with ${Math.round(step.avgConfidence * 100)}% average confidence</span>
        <span class="wwh-label">WHY</span>
        <span class="wwh-value">Structured extraction enables SysML v2 code generation with traceability</span>
        <span class="wwh-label">HOW</span>
        <span class="wwh-value">Claude analyzed text with ${step.hadModelContext ? 'existing model context' : 'standalone extraction'} and produced structured JSON</span>
      </div>
      ${!hasFullData ? html`<span class="preview-only">Preview only — full data not retained for past sessions</span>` : nothing}
      ${step.systemPromptSent ? html`
        <div class="content-block">
          <div class="content-block-header">System Prompt</div>
          <div class="content-block-body"><div class="code-block">${step.systemPromptSent}</div></div>
        </div>
      ` : nothing}
      ${step.contextStringSent ? html`
        <div class="content-block">
          <div class="content-block-header">Model Context</div>
          <div class="content-block-body"><div class="code-block">${step.contextStringSent}</div></div>
        </div>
      ` : nothing}
      ${step.rawResponseText ? html`
        <div class="content-block">
          <div class="content-block-header">Raw AI Response</div>
          <div class="content-block-body"><div class="code-block">${step.rawResponseText}</div></div>
        </div>
      ` : nothing}
      ${hasFullData ? html`
        <div class="content-block">
          <div class="content-block-header">Requirements (${step.count})</div>
          <div class="content-block-body">
            ${step.fullRequirements!.map((r) => this._renderRequirementCard(r))}
          </div>
        </div>
      ` : html`
        <div class="content-block">
          <div class="content-block-header">Requirements Summary (${step.count})</div>
          <div class="content-block-body">
            ${step.summaries.map((s) => html`<div style="font-size: var(--r5-text-xs); padding: 2px 0;">${s.name} — ${s.title}</div>`)}
          </div>
        </div>
      `}
      ${step.confidenceDistribution ? html`
        <div style="font-size: var(--r5-font-size-badge); color: var(--r5-text-muted); margin-top: var(--r5-space-1);">
          Confidence: ${step.confidenceDistribution.high} high, ${step.confidenceDistribution.med} medium, ${step.confidenceDistribution.low} low
        </div>
      ` : nothing}
      ${step.warnings.length > 0 ? html`
        <div style="font-size: var(--r5-font-size-badge); color: var(--r5-warning-700); margin-top: var(--r5-space-1);">
          Warnings (${step.warnings.length}): ${step.warnings.join('; ')}
        </div>
      ` : nothing}
      ${step.apiUsage ? this._renderApiBadges(step.apiUsage) : nothing}
    `;
  }

  private _renderRequirementCard(r: ExtractedRequirement) {
    return html`
      <div class="req-card">
        <div class="req-card-name">${r.name}</div>
        <div class="req-card-title">${r.title}</div>
        <div class="req-card-text">${r.text}</div>
        <div class="req-badges">
          <span class="req-badge req-badge-priority">${r.priority}</span>
          <span class="req-badge req-badge-confidence">${Math.round(r.confidence * 100)}%</span>
          ${r.verificationMethod ? html`<span class="req-badge req-badge-method">${r.verificationMethod}</span>` : nothing}
          ${r.source ? html`<span class="req-badge req-badge-source">${r.source}</span>` : nothing}
        </div>
      </div>
    `;
  }

  // ── User Review ────────────────────────────────────────────────────────

  private _renderUserReview(step: JournalStepUserReview) {
    const hasDeltas = step.decisions.some((d) => d.editDeltas && d.editDeltas.length > 0);

    return html`
      <div class="wwh-grid">
        <span class="wwh-label">WHAT</span>
        <span class="wwh-value">User reviewed ${step.decisions.length} requirements: ${step.acceptedCount} accepted, ${step.editedCount} edited, ${step.rejectedCount} rejected, ${step.removedCount} removed</span>
      </div>
      ${!hasDeltas && step.editedCount > 0 ? html`<span class="preview-only">Edit details not retained for past sessions</span>` : nothing}
      ${step.reviewStartedAt && step.reviewEndedAt ? html`
        <div style="font-size: var(--r5-font-size-badge); color: var(--r5-text-muted); margin-bottom: var(--r5-space-2);">
          Review duration: ${this._formatDuration(new Date(step.reviewEndedAt).getTime() - new Date(step.reviewStartedAt).getTime())}
        </div>
      ` : nothing}
      <div class="decision-list">
        ${step.decisions.map((d) => html`
          <div class="decision-item decision-${d.action}">
            <span class="decision-icon">${this._decisionSvg(d.action)}</span>
            <span>${d.name} — <strong>${d.action}</strong></span>
          </div>
          ${d.editDeltas?.map((delta) => html`
            <div class="delta-row">
              <span class="delta-field">${delta.field}:</span>
              <span class="delta-before">${this._truncate(delta.before, 60)}</span>
              <span class="delta-arrow">&rarr;</span>
              <span class="delta-after">${this._truncate(delta.after, 60)}</span>
            </div>
          `) ?? nothing}
        `)}
      </div>
    `;
  }

  // ── Code Generated ─────────────────────────────────────────────────────

  private _renderCodeGenerated(step: JournalStepCodeGenerated) {
    const codeSource = step.fullCode ?? step.codePreview;
    const isPreviewOnly = !step.fullCode;

    return html`
      <div class="wwh-grid">
        <span class="wwh-label">WHAT</span>
        <span class="wwh-value">Generated ${step.codeLength.toLocaleString()} characters of SysML v2 code</span>
        <span class="wwh-label">WHY</span>
        <span class="wwh-value">Transforms accepted requirements into standards-compliant SysML v2 textual notation</span>
        <span class="wwh-label">HOW</span>
        <span class="wwh-value">${step.method === 'local'
          ? 'Builder schema generated code from structured requirement data (no AI needed)'
          : `Claude AI generated code${step.fallbackReason ? ` — fallback: ${step.fallbackReason}` : ''}`}</span>
      </div>
      ${isPreviewOnly ? html`<span class="preview-only">Preview only — full data not retained for past sessions</span>` : nothing}
      ${step.generationOptions ? html`
        <div class="gen-options">
          <span class="gen-opt">Insert: ${step.generationOptions.insertionPoint}</span>
          ${step.generationOptions.wrapInPackage ? html`<span class="gen-opt">Package: ${step.generationOptions.packageName || 'IngestedRequirements'}</span>` : nothing}
          ${step.generationOptions.includeRationale ? html`<span class="gen-opt">+ Rationale</span>` : nothing}
          ${step.generationOptions.includeSource ? html`<span class="gen-opt">+ Source</span>` : nothing}
        </div>
      ` : nothing}
      ${step.promptSent ? html`
        <div class="content-block">
          <div class="content-block-header">Prompt Sent</div>
          <div class="content-block-body"><div class="code-block">${step.promptSent}</div></div>
        </div>
      ` : nothing}
      <div class="content-block">
        <div class="content-block-header">Generated SysML v2 (${step.codeLength.toLocaleString()} chars)</div>
        <div class="content-block-body"><div class="code-block">${codeSource}</div></div>
      </div>
      <div style="font-size: var(--r5-text-xs); margin-top: var(--r5-space-1); display: flex; gap: var(--r5-space-2); align-items: center;">
        <span style="color: ${step.parseSuccess ? 'var(--r5-success-700)' : 'var(--r5-destructive-700)'}; font-weight: 600;">
          Parse: ${step.parseSuccess ? 'Valid' : 'Invalid'}
        </span>
        <span style="color: var(--r5-text-muted);">${step.diagnosticCount} diagnostics</span>
      </div>
      ${step.fullDiagnostics && step.fullDiagnostics.length > 0 ? html`
        <div class="content-block" style="margin-top: var(--r5-space-2);">
          <div class="content-block-header">Parse Diagnostics</div>
          <div class="content-block-body">
            ${step.fullDiagnostics.map((d) => html`
              <div class="diag-row diag-${d.severity}">
                <span>${d.severity}</span>
                ${d.line != null ? html`<span>L${d.line}</span>` : nothing}
                <span>${d.message}</span>
              </div>
            `)}
          </div>
        </div>
      ` : nothing}
      ${step.apiUsage ? this._renderApiBadges(step.apiUsage) : nothing}
    `;
  }

  // ── User Committed ─────────────────────────────────────────────────────

  private _renderUserCommitted(step: JournalStepUserCommitted) {
    const codeSource = step.fullCode ?? step.codePreview;
    const isPreviewOnly = !step.fullCode;

    return html`
      <div class="wwh-grid">
        <span class="wwh-label">WHAT</span>
        <span class="wwh-value">Committed ${step.requirementCount} requirements to the editor</span>
        <span class="wwh-label">HOW</span>
        <span class="wwh-value">Inserted at "${step.insertionPoint}" position in the active document</span>
      </div>
      ${isPreviewOnly ? html`<span class="preview-only">Preview only — full data not retained for past sessions</span>` : nothing}
      <div class="content-block">
        <div class="content-block-header">Committed Code</div>
        <div class="content-block-body"><div class="code-block">${codeSource}</div></div>
      </div>
    `;
  }

  // ── Error ──────────────────────────────────────────────────────────────

  private _renderError(step: JournalStepError) {
    return html`
      <div class="wwh-grid">
        <span class="wwh-label">WHAT</span>
        <span class="wwh-value" style="color: var(--r5-destructive-700);">Pipeline error in ${step.failedPhase} phase</span>
      </div>
      <div class="code-block" style="background: var(--r5-destructive-50); color: var(--r5-destructive-700);">${step.message}</div>
    `;
  }

  // ── API badges ────────────────────────────────────────────────────────

  private _renderApiBadges(usage: ClaudeApiUsage) {
    return html`
      <div class="api-badges">
        <span class="api-badge">${usage.inputTokens.toLocaleString()} in</span>
        <span class="api-badge">${usage.outputTokens.toLocaleString()} out</span>
        ${usage.cacheTokens > 0 ? html`<span class="api-badge">${usage.cacheTokens.toLocaleString()} cache</span>` : nothing}
        <span class="api-badge">${this._formatDuration(usage.durationMs)}</span>
        <span class="api-badge" style="opacity: 0.7;">${this._shortModel(usage.model)}</span>
        <span class="api-badge" style="opacity: 0.7;">${usage.stopReason}</span>
      </div>
    `;
  }

  // ── Footer ────────────────────────────────────────────────────────────

  private _renderFooter(j: IngestionJournal) {
    const m = j.metrics;
    const totalTokens = m.totalInputTokens + m.totalOutputTokens;
    // Rough cost: ~$3/MTok input, ~$15/MTok output for sonnet
    const cost = (m.totalInputTokens * 3 + m.totalOutputTokens * 15) / 1_000_000;

    return html`
      <div class="trail-footer">
        ${m.extractedCount > 0 ? html`<div class="footer-stat"><span class="footer-stat-value">${m.extractedCount}</span> reqs</div>` : nothing}
        ${m.acceptedCount > 0 ? html`<div class="footer-stat"><span class="footer-stat-value">${m.acceptedCount}</span> accepted</div>` : nothing}
        ${m.apiCallCount > 0 ? html`<div class="footer-stat"><span class="footer-stat-value">${m.apiCallCount}</span> API calls</div>` : nothing}
        ${totalTokens > 0 ? html`<div class="footer-stat"><span class="footer-stat-value">${totalTokens.toLocaleString()}</span> tokens</div>` : nothing}
        ${cost > 0.001 ? html`<div class="footer-stat">~<span class="footer-stat-value">$${cost.toFixed(3)}</span></div>` : nothing}
        <div class="footer-stat"><span class="footer-stat-value">${this._formatDuration(m.totalDurationMs)}</span> total</div>
      </div>
    `;
  }

  // ── Helpers ────────────────────────────────────────────────────────────

  private _stepTitle(step: JournalStep): string {
    switch (step.kind) {
      case 'document-uploaded': return 'Document Uploaded';
      case 'text-extracted': return step.method === 'vision' ? 'Text Extracted (Vision)' : 'Text Loaded';
      case 'requirements-extracted': return `Requirements Extracted (${step.count})`;
      case 'user-review': return `User Review`;
      case 'duplicate-check': return `Duplicate Check`;
      case 'code-generated': return `Code Generated (${step.method})`;
      case 'user-committed': return 'Committed';
      case 'error': return `Error: ${step.message.slice(0, 50)}`;
    }
  }

  private _toggleStep(id: string): void {
    const next = new Set(this._collapsedSteps);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this._collapsedSteps = next;
  }

  private _onClose(): void {
    this.dispatchEvent(new CustomEvent('audit-trail-close', { bubbles: true, composed: true }));
  }

  private _formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  }

  private _formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private _shortModel(model: string): string {
    const match = model.match(/claude-(\w+)-(\d+)/);
    return match ? `${match[1]}-${match[2]}` : model;
  }

  private _truncate(s: string, max: number): string {
    return s.length > max ? s.slice(0, max) + '...' : s;
  }

  private _decisionSvg(action: string) {
    switch (action) {
      case 'accepted':
        return html`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--r5-success-700)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;
      case 'rejected':
        return html`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--r5-text-muted)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
      case 'edited':
        return html`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--r5-primary)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>`;
      case 'removed':
        return html`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--r5-destructive-700)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/></svg>`;
      default:
        return nothing;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-audit-trail': SysmlAuditTrail;
  }
}
